import { z } from "zod"
import {
  ApiSubscriptionStateEnum,
  ApiTierIdEnum,
  TierIdEnum,
  UsageTypeEnum,
} from "./subscription.types"
import { validateDate } from "@beringlab/ui"
import { enableOnlyKorEngNumber } from "@/shared/lib/react-hook-form/react-hook-form.lib"

export const AvailablePlansSchema = z.object({
  slug: z.enum(TierIdEnum),
  label: z.string(),
  price: z.string(),
  features: z.array(z.string()),
  checked: z.boolean().optional(),
})

export const UpgradePlanReqSchema = z.object({
  currentTierId: z.enum(TierIdEnum),
  nextTierId: z.string(),
  subscriptionId: z.string(),
  mode: z.enum(["upgrade", "downgrade"]),
})

export const CancelPlanReqSchema = z.object({
  subscriptionId: z.string(),
  undo: z.boolean(),
})

export const SubscriptionIdSchema = z.object({
  subscriptionId: z.string(),
})

export const ApiSubscriptionSchema = z.object({
  id: z.string(),
  state: z.enum(ApiSubscriptionStateEnum),
  // The list endpoint sends the API tier here, not a marketplace TierId.
  tierId: z.enum(ApiTierIdEnum),
  createDt: z.string(),
  // End of the last active contract; null when none is active. Display only.
  expiresAt: z.string().nullish(),
  // Whether an active contract covers now, by the server's clock.
  contractInForce: z.boolean().optional(),
  // The key itself is stored only as a hash and cannot be shown again; these say
  // whether one exists, its last four characters, and when it was issued.
  hasApiKey: z.boolean().optional(),
  apiKeyHint: z.string().nullish(),
  apiKeyIssuedAt: z.string().nullish(),
})

export const NewCouponSchema = z
  .object({
    isRandomBoxChecked: z.boolean().optional(),
    title: z.string().min(1, { message: "쿠폰명을 입력해주세요." }),
    startDt: z
      .string()
      .min(1, { message: "시작일을 입력해주세요." })
      .refine((value) => validateDate(value), {
        message: "시작일은 YYYYMMDD 형태여야 합니다.",
      }),
    endDt: z
      .string()
      .min(1, { message: "종료일을 입력해주세요." })
      .refine((value) => validateDate(value), {
        message: "종료일은 YYYYMMDD 형태여야 합니다.",
      }),
    code: z.string(),
    usageType: z.union([
      z.literal(UsageTypeEnum.Public),
      z.literal(UsageTypeEnum.Private),
    ]),
    publishCount: z.string(),
    discountRate: z.string().refine(
      (value) => {
        const numberValue = Number(value)
        return numberValue >= 1 && numberValue <= 100
      },
      { message: "1-100 사이의 숫자를 입력해주세요." }
    ),
    maxCount: z.string().refine(
      (value) => {
        const numberValue = Number(value)
        return numberValue >= 1 && numberValue <= 36
      },
      { message: "1-36 사이의 숫자를 입력해주세요." }
    ),
  })
  .refine(
    (data) =>
      data.isRandomBoxChecked ||
      data.usageType !== UsageTypeEnum.Public ||
      enableOnlyKorEngNumber.test(data.code),
    {
      path: ["code"],
      message:
        "코드는 한글, 대문자 영문, 숫자만 포함해야 합니다. 특수문자 및 띄어쓰기는 불가능합니다.",
    }
  )
  .refine(
    (data) =>
      data.isRandomBoxChecked ||
      data.usageType !== UsageTypeEnum.Public ||
      (data.code.length >= 6 && data.code.length <= 20),
    {
      path: ["code"],
      message: "코드는 6자 이상, 20자 이하여야 합니다.",
    }
  )
  .refine(
    (data) =>
      data.usageType !== UsageTypeEnum.Private ||
      (Number(data.publishCount) >= 1 && Number(data.publishCount) <= 100),
    {
      path: ["publishCount"],
      message: "쿠폰 수량은 1 이상, 100 이하여야 합니다.",
    }
  )

export const PaymentMethodChangeProcessSchema = z.object({
  paymentMethodChangeProcess: z.boolean().optional(),
  customerKey: z.string().optional(),
  authKey: z.string().optional(),
})
